import type { AxiosError, AxiosResponse } from 'axios';
import { getNetPositionEndpoint } from '@/api/helpers/get-net-position-endpoint';
import {
  createApiAxios,
  getFabricAuthToken,
} from '@/utilities/axios/api-axios';
import { HEADERS } from '@/utilities/constants/metadata';
import { startTimer } from '@/utilities/helpers/startTimer';
import { useStore } from '@/store/useStore';
import { Logger } from '@/utilities/logger/datadog/rumEvents/rumEvents';
import { DdEventCategory, DdEventType } from '@/utilities/logger/datadog/types';
import { NetPositionResponseType } from './types';

const NET_POSITION_TIMEOUT_MS = 5000;

/**
 * Performs the generic net-position fabric call. Reads userId / auth token from
 * the session store. Returns `null` when the call cannot be made or fails; the
 * caller treats a `null`/error response as a rejected queue entry.
 */
export const fetchNetPosition = async (
  gameUid: string,
): Promise<AxiosResponse<NetPositionResponseType> | null> => {
  const { userId } = useStore.getState().userSessionContext;

  if (!userId) {
    return null;
  }

  const endpoint = getNetPositionEndpoint();

  if (!endpoint) {
    return null;
  }

  const instance = createApiAxios();
  const requestId = crypto.randomUUID().toString();
  const elapsedMs = startTimer();

  try {
    const authToken = await getFabricAuthToken('NET_POSITION');

    return await instance.post<NetPositionResponseType>(
      endpoint,
      {
        requestId,
        authToken,
        userId,
        gameUid,
      },
      {
        timeout: NET_POSITION_TIMEOUT_MS,
        // The FGP netPosition endpoint's CORS config does not allow the
        // Cache-Control header that createApiAxios adds by default; sending
        // it makes the request fail (v2 never sent it). null removes the header.
        headers: { [HEADERS.CACHE_CONTROL]: null },
      },
    );
  } catch (error) {
    const axiosError = error as AxiosError<{ errorCode?: string }>;
    const errorName =
      error instanceof Error && error.message.includes('[GW]')
        ? error.message
        : '[GW][NET_POSITION] Failed';

    Logger.logRumError({
      name: errorName,
      description: 'Generic Net Position call failed',
      useCase: DdEventCategory.UserInteraction,
      eventType: DdEventType.Error,
      context: {
        type: 'NET_POSITION',
        game_uid: gameUid,
        duration: elapsedMs(),
        response_code: axiosError?.response?.status ?? 0,
        error_message: axiosError?.response?.data?.errorCode,
      },
    });

    return null;
  }
};
